import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Menu, MenuItem as MuiMenuItem, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTabContext, TabItem } from '../../contexts/TabContext';
import { getTransientRouteTitle } from './routeComponentMap';
import { isFormDirty } from '../../utils/formLeaveGuard';
import FormLeaveConfirmDialog from './FormLeaveConfirmDialog';

const TAB_HEIGHT = 36;
const TAB_MAX_WIDTH = 200;
const TAB_MIN_WIDTH = 80;
const BAR_HEIGHT = 40;

const TabBar: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
  const {
    tabs, activeTabId, switchTab, closeTab, closeOtherTabs, closeAllTabs,
    togglePinTab, tabLimitReached, pendingTab, confirmOpenTab, cancelOpenTab,
  } = useTabContext();
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; tabId: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // Sekme olarak yönetilmeyen form/detay sayfası açıkken geçici (hayalet) sekme başlığı.
  // Bu durumda normal sekmeler pasif gösterilir; kullanıcı hangi ekranda olduğunu görür.
  const transientTitle = getTransientRouteTitle(location.pathname, i18n.language);

  // Tab limit modal state
  const [selectedToClose, setSelectedToClose] = useState<Set<string>>(new Set());

  const handleContextMenu = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, tabId });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Formdan ayrılma onayı bekleyen sekme
  const [leaveConfirmTab, setLeaveConfirmTab] = useState<TabItem | null>(null);

  const goToTab = useCallback((tab: TabItem) => {
    switchTab(tab.id);
    // Zaten aktif olan sekmeye tıklanırsa activeTabId değişmez ve URL senkronu tetiklenmez.
    // Form/detay sayfasındayken (hayalet sekme) geri dönebilmek için URL farklıysa doğrudan git.
    if (tab.id === activeTabId && location.pathname !== tab.path) {
      navigate(tab.path);
    }
  }, [switchTab, activeTabId, location.pathname, navigate]);

  const handleTabClick = useCallback((tab: TabItem) => {
    // Form sayfası açık ve doluysa ayrılmadan önce onay iste (veriler taslakta saklı)
    if (getTransientRouteTitle(location.pathname) && isFormDirty()) {
      setLeaveConfirmTab(tab);
      return;
    }
    goToTab(tab);
  }, [goToTab, location.pathname]);

  const handleCloseTab = useCallback((e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    closeTab(tabId);
  }, [closeTab]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  // Tab limit modal handlers
  const handleToggleSelect = (tabId: string) => {
    setSelectedToClose(prev => {
      const next = new Set(prev);
      if (next.has(tabId)) {
        next.delete(tabId);
      } else {
        next.add(tabId);
      }
      return next;
    });
  };

  const handleConfirmClose = () => {
    if (selectedToClose.size > 0) {
      confirmOpenTab(Array.from(selectedToClose));
      setSelectedToClose(new Set());
    }
  };

  const handleCancelModal = () => {
    cancelOpenTab();
    setSelectedToClose(new Set());
  };

  const closableTabs = tabs.filter(t => !t.pinned);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 56,
          left: isCollapsed ? 70 : 260,
          right: 0,
          height: BAR_HEIGHT,
          bgcolor: '#dee1e6',
          display: 'flex',
          alignItems: 'flex-end',
          zIndex: 98,
          transition: (theme) => theme.transitions.create(['left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '@media (max-width: 1399px)': {
            left: 0,
          },
          '@media (max-width: 999px)': {
            display: 'none',
          },
        }}
      >
        <Box
          ref={scrollRef}
          onWheel={handleWheel}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            overflowX: 'auto',
            overflowY: 'hidden',
            flex: 1,
            height: '100%',
            px: 0.5,
            gap: '1px',
            '&::-webkit-scrollbar': { height: 0, display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {tabs.map((tab) => {
            const isActive = !transientTitle && tab.id === activeTabId;
            return (
              <Box
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                onContextMenu={(e) => handleContextMenu(e, tab.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: TAB_MIN_WIDTH,
                  maxWidth: TAB_MAX_WIDTH,
                  height: isActive ? TAB_HEIGHT : TAB_HEIGHT - 2,
                  px: 1.2,
                  cursor: 'pointer',
                  position: 'relative',
                  userSelect: 'none',
                  borderRadius: '8px 8px 0 0',
                  bgcolor: isActive ? '#fff' : 'transparent',
                  mb: 0,
                  transition: 'background-color 0.15s, height 0.15s',
                  '&:hover': {
                    bgcolor: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
                    height: TAB_HEIGHT - 1,
                  },
                }}
              >
                {tab.pinned && (
                  <PushPinIcon sx={{ 
                    fontSize: 13, 
                    color: isActive ? '#5f6368' : '#80868b',
                    transform: 'rotate(45deg)',
                    mr: 0.3,
                    flexShrink: 0,
                  }} />
                )}
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: '0.8rem',
                    color: isActive ? '#1a1a1a' : '#5f6368',
                    fontWeight: isActive ? 500 : 400,
                    lineHeight: `${TAB_HEIGHT}px`,
                    letterSpacing: '0.01em',
                  }}
                >
                  {tab.title}
                </Typography>
                {!tab.pinned && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleCloseTab(e, tab.id)}
                    sx={{
                      p: 0.3,
                      ml: 0.3,
                      color: isActive ? '#5f6368' : '#80868b',
                      borderRadius: '50%',
                      flexShrink: 0,
                      '&:hover': {
                        color: '#202124',
                        bgcolor: isActive ? '#e8eaed' : 'rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {/* Geçici (hayalet) sekme: form/detay sayfası açıkken sayfanın adını gösterir */}
          {transientTitle && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: TAB_MIN_WIDTH,
                maxWidth: TAB_MAX_WIDTH,
                height: TAB_HEIGHT,
                px: 1.2,
                userSelect: 'none',
                borderRadius: '8px 8px 0 0',
                bgcolor: '#fff',
                flexShrink: 0,
              }}
            >
              <EditNoteIcon sx={{ fontSize: 16, color: '#5f6368', flexShrink: 0 }} />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  fontSize: '0.8rem',
                  color: '#1a1a1a',
                  fontWeight: 500,
                  lineHeight: `${TAB_HEIGHT}px`,
                  letterSpacing: '0.01em',
                  fontStyle: 'italic',
                }}
              >
                {transientTitle}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                minWidth: 180,
              }
            }
          }}
        >
          {/* Pin / Unpin */}
          {contextMenu && (() => {
            const targetTab = tabs.find(t => t.id === contextMenu.tabId);
            if (!targetTab) return null;
            const isDefaultTab = targetTab.path === '/accounting/dashboard';
            return (
              <MuiMenuItem
                onClick={() => {
                  togglePinTab(contextMenu.tabId);
                  handleCloseContextMenu();
                }}
                disabled={isDefaultTab}
                sx={{ fontSize: '0.85rem', py: 0.8 }}
              >
                {targetTab.pinned ? (
                  <>
                    <PushPinOutlinedIcon sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                    Sabitten Kaldır
                  </>
                ) : (
                  <>
                    <PushPinIcon sx={{ fontSize: 16, mr: 1, color: '#5f6368', transform: 'rotate(45deg)' }} />
                    Sekmeyi Sabitle
                  </>
                )}
              </MuiMenuItem>
            );
          })()}
          <Divider />
          <MuiMenuItem
            onClick={() => {
              if (contextMenu) closeTab(contextMenu.tabId);
              handleCloseContextMenu();
            }}
            disabled={contextMenu ? tabs.find(t => t.id === contextMenu.tabId)?.pinned : false}
            sx={{ fontSize: '0.85rem', py: 0.8 }}
          >
            Sekmeyi Kapat
          </MuiMenuItem>
          <MuiMenuItem
            onClick={() => {
              if (contextMenu) closeOtherTabs(contextMenu.tabId);
              handleCloseContextMenu();
            }}
            sx={{ fontSize: '0.85rem', py: 0.8 }}
          >
            Diğer Sekmeleri Kapat
          </MuiMenuItem>
          <Divider />
          <MuiMenuItem
            onClick={() => {
              closeAllTabs();
              handleCloseContextMenu();
            }}
            sx={{ fontSize: '0.85rem', py: 0.8 }}
          >
            Tüm Sekmeleri Kapat
          </MuiMenuItem>
        </Menu>
      </Box>

      {/* Tab Limit Modal */}
      <Dialog
        open={tabLimitReached}
        onClose={handleCancelModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberIcon color="warning" />
          Sekme Limiti
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Maksimum 15 sekme açık. 
            {pendingTab && <><br /><strong>"{pendingTab.title}"</strong> sekmesini açmak için aşağıdan kapatılacak sekmeleri seçin.</>}
          </Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {closableTabs.map(tab => (
              <ListItem key={tab.id} disablePadding>
                <ListItemButton
                  onClick={() => handleToggleSelect(tab.id)}
                  dense
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedToClose.has(tab.id)}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={tab.title}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {closableTabs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Kapatılabilecek sekme yok. Sabitlenmiş sekmelerin sabitini kaldırın.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelModal} color="inherit">
            İptal
          </Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            disabled={selectedToClose.size === 0}
          >
            {selectedToClose.size > 0
              ? `${selectedToClose.size} Sekmeyi Kapat ve Aç`
              : 'Sekme Seçin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formdan ayrılma onayı */}
      <FormLeaveConfirmDialog
        open={leaveConfirmTab !== null}
        onCancel={() => setLeaveConfirmTab(null)}
        onConfirm={() => {
          if (leaveConfirmTab) goToTab(leaveConfirmTab);
          setLeaveConfirmTab(null);
        }}
      />
    </>
  );
};

export default React.memo(TabBar);
